'use client';

import { c as _c } from "react/compiler-runtime";
import { Checkbox, Divider, Stack, Typography } from '@mui/material';
import type { ClassificationCategory, Condition, DataClassificationEntityOption } from '@repo/api-gw-sdk';
import { useMemo } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { HelpIcon } from '@/components/shared/helpIcon';
import { Loader } from '@/components/shared/loader';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { PiiStrings } from '@/data/inventory/data';
import { useClassificationCategories } from '@/data/inventory/useClassificationCategories';
import { createValueCondition, findValueCondition, isValueCondition } from '@/types/advanceFilter';
import { MultiSelectFilter, type FilterProps } from './filters';
const DATA_CLASSES = 'dataClasses';
const DATA_CLASS_ENTITIES = 'dataClassEntities';
const OTHER_CATEGORY = 'Other';
const DEFAULT_CLASS_NAMES = ['PII', 'FI', 'PHI'];
const DEFAULT_CLASS_ORDER = new Map(DEFAULT_CLASS_NAMES.map((name, index) => [name, index]));
const isDefaultClass = (name: string) => DEFAULT_CLASS_NAMES.includes(name);
const isCustomClass = (name: string) => !isDefaultClass(name) && name !== OTHER_CATEGORY;
const sortDefaultClasses = <T extends {
  id: string;
},>(classes: T[]) => [...classes].sort((a, b) => (DEFAULT_CLASS_ORDER.get(a.id) ?? DEFAULT_CLASS_NAMES.length) - (DEFAULT_CLASS_ORDER.get(b.id) ?? DEFAULT_CLASS_NAMES.length));
interface DataClassGroup {
  id: string;
  description?: string;
  children: string[];
}
const buildGroups = (categories: ClassificationCategory[], entities: DataClassificationEntityOption[]): DataClassGroup[] => {
  const groups = new Map<string, DataClassGroup>(categories.map(c => [c.name, {
    id: c.name,
    description: c.description,
    children: []
  }]));
  for (const entity of entities) {
    const cats = entity.categories.length ? entity.categories : [OTHER_CATEGORY];
    for (const cat of cats) {
      const group = groups.get(cat) ?? {
        id: cat,
        children: []
      };
      group.children.push(entity.name);
      groups.set(cat, group);
    }
  }
  return [...groups.values()].map(group => ({
    ...group,
    children: [...new Set(group.children)].sort()
  }));
};
const findValues = (conditions: Condition[], property: string): string[] => findValueCondition(conditions, property)?.value ?? [];
const isDataClassCondition = (condition: Condition) => isValueCondition(condition) && (condition.property === DATA_CLASSES || condition.property === DATA_CLASS_ENTITIES);
const buildDataClassConditions = (classes: string[], entities: string[]): Condition[] => {
  const out: Condition[] = [];
  if (classes.length) {
    out.push(createValueCondition(DATA_CLASSES, classes));
  }
  if (entities.length) {
    out.push(createValueCondition(DATA_CLASS_ENTITIES, entities));
  }
  return out;
};
const stripDataClassConditions = (conditions: Condition[]): Condition[] => conditions.filter(c => !isDataClassCondition(c));
const ROW_GAP = '12px';
const ROW_HORIZONTAL_PADDING = '0px';
const GROUP_VERTICAL_PADDING = '12px';
const HEADER_VERTICAL_PADDING = '8px';
const CHILD_VERTICAL_PADDING = '4px';
const CHECKBOX_PADDING = '2px';
const CONTROL_SLOT_WIDTH = '24px';
const CHILD_PADDING_LEFT = `calc(${CONTROL_SLOT_WIDTH} + ${ROW_GAP})`;
export const entityLabel = (entity: string) => PiiStrings[entity] ?? entity;
const CUSTOM_CLASS_SUBTITLE = 'Manually added by Eon for your organization';
const CustomClassTooltip = t0 => {
  const $ = _c(5);
  const {
    description
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Divider sx={{
      borderColor: "currentColor",
      opacity: 0.2
    }} />;
    t2 = {
      gap: "8px"
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Stack direction="row" sx={{
      gap: "8px",
      alignItems: "center"
    }}><i className="material-symbols-settings-outline-rounded text-[24px]" />{CUSTOM_CLASS_SUBTITLE}</Stack>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== description) {
    t4 = <Stack divider={t1} sx={t2}>{description}{t3}</Stack>;
    $[3] = description;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
const DataClassFilterGroup = t0 => {
  const $ = _c(44);
  const {
    group,
    isCustom,
    parentSelected,
    selectedEntities,
    onParentChange,
    onChildChange
  } = t0;
  let t1;
  if ($[0] !== group.children || $[1] !== selectedEntities) {
    let t2;
    if ($[3] !== selectedEntities) {
      t2 = c => selectedEntities.includes(c);
      $[3] = selectedEntities;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = group.children.some(t2);
    $[0] = group.children;
    $[1] = selectedEntities;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const childSelected = t1;
  const hasChildren = group.children.length > 0;
  const t2 = `material-symbols-arrow-right-rounded${hasChildren ? "" : " invisible"}`;
  let t3;
  if ($[5] !== t2) {
    t3 = <i className={t2} />;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      gap: ROW_GAP,
      height: "auto",
      minHeight: "auto",
      padding: `${GROUP_VERTICAL_PADDING} ${ROW_HORIZONTAL_PADDING}`,
      flexDirection: "row-reverse",
      "&.Mui-expanded": {
        minHeight: "auto"
      },
      "& .MuiAccordionSummary-content": {
        margin: "0px",
        minWidth: "0px",
        paddingLeft: "0px"
      },
      "& .MuiAccordionSummary-expandIconWrapper": {
        alignItems: "center",
        height: CONTROL_SLOT_WIDTH,
        justifyContent: "center",
        width: CONTROL_SLOT_WIDTH
      }
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      alignItems: "center",
      gap: ROW_GAP,
      minWidth: "0px",
      width: "100%"
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `filter-dataClasses-${group.id}`;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      padding: CHECKBOX_PADDING
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = !parentSelected && childSelected;
  let t9;
  if ($[10] !== onParentChange || $[11] !== parentSelected || $[12] !== t6 || $[13] !== t8) {
    t9 = <Checkbox data-testid={t6} sx={t7} size="small" checked={parentSelected} indeterminate={t8} onClick={_temp} onChange={onParentChange} />;
    $[10] = onParentChange;
    $[11] = parentSelected;
    $[12] = t6;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== onParentChange) {
    t10 = (text, ref) => <Typography ref={ref} variant="subtitle1" className="cursor-pointer truncate" sx={{
      minWidth: "0px"
    }} onClick={e_0 => {
      e_0.stopPropagation();
      onParentChange();
    }}>{text}</Typography>;
    $[15] = onParentChange;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== group.id || $[18] !== t10) {
    t11 = <OverflowTooltip text={group.id} renderContent={t10} />;
    $[17] = group.id;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      marginLeft: "auto",
      justifyContent: "center"
    };
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== group.description || $[22] !== group.id || $[23] !== isCustom) {
    t13 = isCustom ? <CustomClassTooltip description={group.description} /> : group.description ?? group.id;
    $[21] = group.description;
    $[22] = group.id;
    $[23] = isCustom;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      slotProps: {
        tooltip: {
          sx: {
            padding: "16px"
          }
        }
      }
    };
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t13) {
    t15 = <Stack onClick={_temp2} sx={t12}><HelpIcon title={t13} className="cursor-default" tooltipProps={t14} /></Stack>;
    $[26] = t13;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t11 || $[29] !== t15 || $[30] !== t9) {
    t16 = <Stack direction="row" className="group/help" sx={t5}>{t9}{t11}{t15}</Stack>;
    $[28] = t11;
    $[29] = t15;
    $[30] = t9;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t16 || $[33] !== t3) {
    t17 = <AccordionSummary expandIcon={t3} sx={t4}>{t16}</AccordionSummary>;
    $[32] = t16;
    $[33] = t3;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== group.children || $[36] !== hasChildren || $[37] !== onChildChange || $[38] !== parentSelected || $[39] !== selectedEntities) {
    t18 = hasChildren && <AccordionDetails sx={{
      padding: "0px"
    }}><Stack sx={{
        gap: "0px"
      }}>{group.children.map(entity => <Stack key={entity} direction="row" sx={{
          alignItems: "center",
          gap: ROW_GAP,
          padding: `${CHILD_VERTICAL_PADDING} ${ROW_HORIZONTAL_PADDING} ${CHILD_VERTICAL_PADDING} ${CHILD_PADDING_LEFT}`
        }}><Checkbox data-testid={`filter-dataClassEntities-${entity}`} sx={{
            padding: CHECKBOX_PADDING
          }} size="small" checked={parentSelected || selectedEntities.includes(entity)} onChange={() => onChildChange(entity)} /><OverflowTooltip text={entityLabel(entity)} renderContent={(text_0, ref_0) => <Typography ref={ref_0} variant="body2" className="cursor-pointer truncate" sx={{
            minWidth: "0px"
          }} onClick={() => onChildChange(entity)}>{text_0}</Typography>} /></Stack>)}</Stack></AccordionDetails>;
    $[35] = group.children;
    $[36] = hasChildren;
    $[37] = onChildChange;
    $[38] = parentSelected;
    $[39] = selectedEntities;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t17 || $[42] !== t18) {
    t19 = <Accordion>{t17}{t18}</Accordion>;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  return t19;
};
const DataClassGroupsWrapper = t0 => {
  const $ = _c(16);
  const {
    groups,
    isCustom,
    selectedClasses,
    selectedEntities,
    onParentChange,
    onChildChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "0px",
      paddingY: GROUP_VERTICAL_PADDING
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== groups || $[2] !== isCustom || $[3] !== onChildChange || $[4] !== onParentChange || $[5] !== selectedClasses || $[6] !== selectedEntities) {
    let t3;
    if ($[8] !== isCustom || $[9] !== onChildChange || $[10] !== onParentChange || $[11] !== selectedClasses || $[12] !== selectedEntities) {
      t3 = group => <DataClassFilterGroup key={group.id} group={group} isCustom={isCustom} parentSelected={selectedClasses.includes(group.id)} selectedEntities={selectedEntities} onParentChange={() => onParentChange(group)} onChildChange={entity => onChildChange(group, entity)} />;
      $[8] = isCustom;
      $[9] = onChildChange;
      $[10] = onParentChange;
      $[11] = selectedClasses;
      $[12] = selectedEntities;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    t2 = groups.map(t3);
    $[1] = groups;
    $[2] = isCustom;
    $[3] = onChildChange;
    $[4] = onParentChange;
    $[5] = selectedClasses;
    $[6] = selectedEntities;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[14] !== t2) {
    t3 = <Stack sx={t1}>{t2}</Stack>;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
};
const DataClassesWithEntitiesFilter = ({
  conditions,
  onChange
}: FilterProps) => {
  const dal = useDAL();
  const {
    categories,
    isLoading: categoriesLoading
  } = useClassificationCategories();
  const {
    data: body,
    isLoading: entitiesLoading
  } = dal.inventory.entities.useList();
  const groups = useMemo(() => buildGroups(categories, body?.entities ?? []), [categories, body?.entities]);
  if (categoriesLoading || entitiesLoading) {
    return <Loader />;
  }
  const selectedClasses = findValues(conditions, DATA_CLASSES);
  const selectedEntities = findValues(conditions, DATA_CLASS_ENTITIES);
  const apply = (classes: string[], entities: string[]) => {
    onChange([...stripDataClassConditions(conditions), ...buildDataClassConditions(classes, entities)]);
  };
  const onParentChange = (group: DataClassGroup) => {
    const isOn = selectedClasses.includes(group.id);
    const nextClasses = isOn ? selectedClasses.filter(c => c !== group.id) : [...selectedClasses, group.id];
    const nextEntities = selectedEntities.filter(e => !group.children.includes(e));
    apply(nextClasses, nextEntities);
  };
  const onChildChange = (group_0: DataClassGroup, entity: string) => {
    const nextClasses_0 = selectedClasses.filter(c_0 => c_0 !== group_0.id);
    const otherEntities = selectedEntities.filter(e_0 => !group_0.children.includes(e_0));
    // Selecting a child while the parent class is selected swaps the broad
    // class for that single entity in the same group.
    if (selectedClasses.includes(group_0.id)) {
      apply(nextClasses_0, [...otherEntities, entity]);
      return;
    }
    const nextEntities_0 = selectedEntities.includes(entity) ? selectedEntities.filter(e_1 => e_1 !== entity) : [...selectedEntities, entity];
    apply(nextClasses_0, nextEntities_0);
  };
  const hasCustoms = categories.some(c_1 => isCustomClass(c_1.name));
  const defaultGroups = sortDefaultClasses(groups.filter(g => isDefaultClass(g.id)));
  const customGroups = groups.filter(g_0 => isCustomClass(g_0.id));
  const otherGroups = groups.filter(g_1 => !isDefaultClass(g_1.id) && !isCustomClass(g_1.id));
  return <Stack sx={{
    gap: '0px'
  }}>
      {hasCustoms && <Typography variant='overline' sx={{
      padding: `${HEADER_VERTICAL_PADDING} ${ROW_HORIZONTAL_PADDING}`
    }}>
          Default classes
        </Typography>}
      <DataClassGroupsWrapper groups={defaultGroups} isCustom={false} selectedClasses={selectedClasses} selectedEntities={selectedEntities} onParentChange={onParentChange} onChildChange={onChildChange} />
      {hasCustoms && <>
          <Typography variant='overline' sx={{
        padding: `${HEADER_VERTICAL_PADDING} ${ROW_HORIZONTAL_PADDING}`
      }}>
            Custom classes
          </Typography>
          <DataClassGroupsWrapper groups={customGroups} isCustom={true} selectedClasses={selectedClasses} selectedEntities={selectedEntities} onParentChange={onParentChange} onChildChange={onChildChange} />
        </>}
      {otherGroups.length > 0 && <DataClassGroupsWrapper groups={otherGroups} isCustom={false} selectedClasses={selectedClasses} selectedEntities={selectedEntities} onParentChange={onParentChange} onChildChange={onChildChange} />}
    </Stack>;
};
const FlatDataClassesFilter = props => {
  const $ = _c(8);
  const {
    categories,
    isLoading
  } = useClassificationCategories();
  let t0;
  if ($[0] !== categories) {
    bb0: {
      const toOption = _temp3;
      const defaults = categories.filter(_temp4).map(toOption);
      const sortedDefaults = sortDefaultClasses(defaults);
      const customs = categories.filter(_temp5).map(toOption);
      if (customs.length === 0) {
        t0 = sortedDefaults;
        break bb0;
      }
      let t1;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          id: "__default_header",
          name: "Default classes",
          isGroupTitle: true
        };
        $[2] = t1;
      } else {
        t1 = $[2];
      }
      let t2;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {
          id: "__custom_header",
          name: "Custom classes",
          isGroupTitle: true
        };
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      t0 = [t1, ...sortedDefaults, t2, ...customs];
    }
    $[0] = categories;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const options = t0;
  if (isLoading) {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Loader />;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== options || $[6] !== props) {
    t1 = <MultiSelectFilter {...props} options={options} />;
    $[5] = options;
    $[6] = props;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
};
export const DataClassesFilter = props => {
  const $ = _c(3);
  const {
    EnableInventoryEntitiesFilter
  } = useFeatureFlags();
  let t0;
  if ($[0] !== EnableInventoryEntitiesFilter || $[1] !== props) {
    t0 = EnableInventoryEntitiesFilter === true ? <DataClassesWithEntitiesFilter {...props} /> : <FlatDataClassesFilter {...props} />;
    $[0] = EnableInventoryEntitiesFilter;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_1) {
  return e_1.stopPropagation();
}
function _temp3(c) {
  return {
    id: c.name,
    name: c.name,
    description: isCustomClass(c.name) ? <CustomClassTooltip description={c.description} /> : c.description
  };
}
function _temp4(c_0) {
  return isDefaultClass(c_0.name);
}
function _temp5(c_1) {
  return isCustomClass(c_1.name);
}